import { currentLanguage, getDateFormatter } from "./locale";

/**
 * Dates, through Intl and the reader's own language. Never the browser default, and never a
 * hard-coded English locale: the page is read in six.
 *
 * DS35's shapes: a date in a line is "2 October 2026", a date and a time is "2 October 2026,
 * 18:30". The month is a word and never a number, so 2/10 and 10/2 cannot be read as each other.
 */

/** "2 October 2026". Returns null when there is no date, so the caller draws nothing. */
export function formatLongDate(
  value?: string | null,
  lang: string = currentLanguage(),
): string | null {
  const date = toDate(value);
  if (!date) return null;
  return getDateFormatter(lang, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** "2 October 2026, 18:30", for a receipt that has to say when. */
export function formatDateTimeLine(
  value?: string | null,
  lang: string = currentLanguage(),
): string | null {
  const date = toDate(value);
  if (!date) return null;
  return getDateFormatter(lang, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export type DueTone = "secondary" | "danger";

export interface DueLine {
  tone: DueTone;
  /** Which sentence the caller should draw; the words themselves come from the keys file. */
  kind: "future" | "today" | "overdue";
  date: string;
}

/**
 * The moment line under an invoice's amount: due in the future, due today, or overdue since.
 *
 * Only the overdue case is danger; an invoice that is simply not paid yet is not news, so it
 * takes the secondary ink (status-as-text 1.13, and DS35's rule that a state running normally
 * carries no colour). Returns null when the invoice carries no due date at all, and the page
 * then draws nothing rather than inventing "on receipt".
 */
export function dueLine(
  dueDate?: string | null,
  lang: string = currentLanguage(),
  now: Date = new Date(),
): DueLine | null {
  const date = toDate(dueDate);
  if (!date) return null;
  const formatted = formatLongDate(dueDate, lang);
  if (!formatted) return null;

  const startOfDue = startOfDay(date);
  const startOfNow = startOfDay(now);

  if (startOfDue.getTime() === startOfNow.getTime()) {
    return { tone: "secondary", kind: "today", date: formatted };
  }
  if (startOfDue.getTime() < startOfNow.getTime()) {
    return { tone: "danger", kind: "overdue", date: formatted };
  }
  return { tone: "secondary", kind: "future", date: formatted };
}

/**
 * Today plus one billing period, for the "next on {{date}}" caption. The backend does not send
 * the next charge date on the checkout payload, so it is computed here from the cadence the
 * price carries, which is the same arithmetic Stripe will do.
 */
export function addInterval(
  from: Date,
  interval: string,
  intervalCount: number,
): Date {
  const next = new Date(from.getTime());
  const count = Math.max(1, Math.round(intervalCount));
  switch (interval) {
    case "day":
      next.setDate(next.getDate() + count);
      break;
    case "week":
      next.setDate(next.getDate() + count * 7);
      break;
    case "month":
      next.setMonth(next.getMonth() + count);
      break;
    case "year":
      next.setFullYear(next.getFullYear() + count);
      break;
    default:
      return next;
  }
  return next;
}

export function isFuture(
  value?: string | null,
  now: Date = new Date(),
): boolean {
  const date = toDate(value);
  return !!date && date.getTime() > now.getTime();
}

function toDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
